import React, { memo, useMemo, useState } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Input from "@react/shared/ui/Input"
import Badge from "@react/shared/ui/Badge"
import { usePlanning } from "./PlanningContext"
import { matches } from "../lib/search"
import { formatHours } from "../lib/hours"

const ProjectItem = memo(({ project, hours, highlighted, onToggle }) => {
  const { handlers, canWrite } = usePlanning()
  return (
    <li
      onClick={() => onToggle(project.id)}
      aria-pressed={highlighted}
      draggable={canWrite}
      onDragStart={event => handlers.dragProject(project, event)}
      onDragEnd={handlers.dragEnd}
      title={[project.title, project.client, project.address, project.manager].filter(Boolean).join("\n")}
      className={clsx(
        "group flex items-start gap-2.5 px-2.5 py-2 rounded-lg border transition-[transform,border-color,background-color] duration-150 select-none",
        canWrite && "cursor-grab active:cursor-grabbing hover:-translate-y-px",
        highlighted ? "border-brand-300 bg-brand-50" : "border-transparent hover:border-slate-200 hover:bg-slate-50",
      )}
    >
      <span className="mt-1 w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-white" style={{ backgroundColor: project.color }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          {project.code && <span className="font-mono text-[10.5px] font-semibold text-slate-500 shrink-0">{project.code}</span>}
          <span className="text-[12.5px] font-semibold text-slate-800 truncate">{project.name}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
          <span className="text-[11px] text-slate-500 truncate">{project.client || project.address || " "}</span>
          {project.zone && <Badge size="xs" tone="slate" className="ml-auto shrink-0">{I18n.t("project_planning.zone", { zone: project.zone })}</Badge>}
        </div>
        {hours > 0 && (
          <div className="mt-1 text-[10.5px] font-medium text-slate-400 tabular-nums">
            {I18n.t("project_planning.palette.planned", { hours: formatHours(hours) })}
          </div>
        )}
      </div>
      {canWrite && <FontAwesomeIcon icon="grip-vertical" className="mt-1 text-[10px] text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />}
    </li>
  )
})

const ProjectPalette = ({ projects, hoursByProject, highlightProjectId, onToggle }) => {
  const [query, setQuery] = useState("")
  const visible = useMemo(() => projects.filter(project =>
    !["completed", "cancelled"].includes(project.status) &&
    matches(query, project.code, project.name, project.client, project.address)), [projects, query])

  return (
    <>
      <div className="px-3 pt-3 pb-2 border-b border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[13px] font-semibold text-slate-900">{I18n.t("project_planning.palette.projects")}</h2>
          <span className="text-[11px] font-semibold text-slate-400 tabular-nums">{visible.length}</span>
        </div>
        <Input variant="search" size="sm" leadingIcon="magnifying-glass" placeholder={I18n.t("project_planning.palette.search_projects")} value={query} onChange={event => setQuery(event.target.value)} />
      </div>
      <ul className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
        {visible.map(project => (
          <ProjectItem key={project.id} project={project} hours={hoursByProject.get(project.id) || 0} highlighted={project.id === highlightProjectId} onToggle={onToggle} />
        ))}
        {visible.length === 0 && <li className="px-3 py-8 text-center text-[12px] text-slate-400">{I18n.t("project_planning.no_match")}</li>}
      </ul>
    </>
  )
}

export default ProjectPalette
